import { Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, Link } from "@/lib/router-compat";
import { useFormFocusVisible } from "@/hooks/useFormFocusVisible";
import { PHONE_TEL_HREF } from "@/config/business";
import {
  ESTIMATOR_HREF,
  PRIMARY_CTA_LABEL,
  scrollToEstimator,
} from "@/lib/primaryCta";

/**
 * Sticky mobile action bar — Phase 1.
 *
 * Two actions only: dominant "Get My Fence Price" (scrolls to the on-page
 * estimator, or navigates to /#pricing-estimator) and a secondary Call.
 * No numeric social proof is rendered. Hidden while a form field is focused
 * and while the estimator (or booking widget) is already on screen.
 */
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(true);
  const location = useLocation();
  const isOnBookPage = location.pathname === "/book";
  const formFocused = useFormFocusVisible();

  useEffect(() => {
    let ticking = false;
    let lastVisible = true;

    const measure = () => {
      ticking = false;
      const target = isOnBookPage
        ? document.getElementById("booking-widget")
        : document.getElementById("pricing-estimator") ||
          document.getElementById("schedule");
      if (target) {
        const rect = target.getBoundingClientRect();
        const next = rect.top > window.innerHeight || rect.bottom < 0;
        if (next !== lastVisible) {
          lastVisible = next;
          setVisible(next);
        }
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(measure);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    measure();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOnBookPage]);

  if (!visible || formFocused) return null;

  const handlePrimary = (e: React.MouseEvent) => {
    void import("@/lib/analytics").then(({ trackEvent }) =>
      trackEvent("sticky_quote_tap", { placement: "sticky_mobile_primary" }),
    );
    if (isOnBookPage) {
      const widget = document.getElementById("booking-widget");
      if (widget) {
        e.preventDefault();
        widget.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    scrollToEstimator(e);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-xs border-t border-border shadow-lg px-3 py-2.5"
      style={{ paddingBottom: `calc(0.625rem + env(safe-area-inset-bottom))` }}
    >
      <div className="flex items-stretch gap-2">
        <a
          href={ESTIMATOR_HREF}
          onClick={handlePrimary}
          className="flex-1 flex items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold text-sm min-h-11 px-3 active:scale-[0.98] transition-transform text-center"
        >
          {isOnBookPage ? "Open Calendar" : PRIMARY_CTA_LABEL}
        </a>
        <Link
          to="/order"
          className="flex-1 flex items-center justify-center rounded-md border-2 border-primary text-primary font-semibold text-sm min-h-11 px-3 active:scale-[0.98] transition-transform text-center"
        >
          Reserve Your Fence Install
        </Link>
        <a
          href={PHONE_TEL_HREF}
          aria-label="Call Modern Fence Install"
          onClick={() => {
            void import("@/lib/analytics").then(({ conversions }) =>
              conversions.phoneClick("sticky-mobile"),
            );
          }}
          className="flex items-center justify-center rounded-md border border-primary/30 text-primary font-semibold text-sm min-h-11 px-3.5 active:scale-[0.98] transition-transform"
        >
          <Phone className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

export default StickyMobileCTA;
